import { assert } from '../util/assert';
import * as Handlebars from 'handlebars';
import { getRouteImageUrl, Destination } from "../util/route-data";
import { getById, querySelector, setShown } from "../util/dom";
import { isSet, roundToDecimal } from "../util/types";
import { CURRENT_MILE_UNKNOWN, State, StateUpdate } from "./state";
import { View, ViewType } from "./view";
import { ScrollIndicator } from './scroll_indicator';
import { historyManager } from '@src/util/history_state';
import { scrollToAnimated } from '@src/util/scroll_to_animated';

const HOURS_PER_MS = 60 * 60 * 1000;

// The height in pixels of the banner that covers scrollable content.
const BANNER_HEIGHT = 100;

interface SpotRef {
  metadata: Destination;
  el: HTMLElement;
  etaEl: HTMLElement;
  distanceEl: HTMLElement;
  shown: boolean;
  scrollPos: number;
}

export class RouteView extends View {
  // The spots that were in the current viewport when the view was deactivated.
  // This is used when re-showing RouteView after toggling categories, since
  // the knowing the previous scroll position is unhelpful in that situation.
  private prevVisibleSpots: SpotRef[] = [];
  private spots: SpotRef[];
  private shownSpots: SpotRef[] = [];
  private windowWidth = 0;
  private windowHeight = 0;
  private scrollIndicator: ScrollIndicator;
  private scrollAnimatorCancel?: () => void;

  private mileMarkerIsTraveled = true;

  getType(): ViewType {
    return ViewType.ROUTE;
  }

  constructor(protected readonly state: State,
    protected readonly requestLocationUpdate: () => void,
    readonly el: HTMLElement) {
    super(state, requestLocationUpdate, el);

    this.createIconStyles();

    // Render spots.
    const source = getById('spots-template').innerHTML;
    const template = Handlebars.compile(source);
    const kmzSpots = this.state.kmz.spots;
    const spotsEl = getById('spots');
    spotsEl.innerHTML = template({ spots: kmzSpots });

    // Init spots array.
    this.spots = [];
    for (let i = 0; i < kmzSpots.length; i++) {
      const el = spotsEl.children[i];
      this.spots.push({
        metadata: kmzSpots[i],
        el: (el as HTMLElement),
        etaEl: el.querySelector('.eta-val') as HTMLElement,
        distanceEl: el.querySelector('.distance-num') as HTMLElement,
        shown: true,
        scrollPos: 0, // populated by handleResize
      });
    }
    this.toggleVisibleCategories();

    getById('hamburger').addEventListener('click',
      () => this.navigate(ViewType.MENU));
    getById('scroll-to-location').addEventListener('click', () => {
      if (this.scrollAnimatorCancel) this.scrollAnimatorCancel();
      const scrollY = this.getCurrentLocationScroll();
      if (scrollY !== null) {
        this.scrollAnimatorCancel = scrollToAnimated(scrollY, this.scrollIndicator);
      }
    });
    getById('mile-marker').addEventListener('click',
      () => this.toggleMileMarkerMode());
    querySelector('#route .location-error').addEventListener('click',
      () => this.requestLocationUpdate());

    this.handleUpdatedDistance();

    this.scrollIndicator =
      new ScrollIndicator(this.getScrollIndicatorText.bind(this));
    window.addEventListener('resize', () => this.handleResize({}));
  }

  /** The return value is the scroll position we want to take the user to. */
  protected renderUpdates(stateUpdate: StateUpdate): number {
    this.renderStateUpdate(stateUpdate);

    // Decide where to scroll to if the categories have changed.
    if (stateUpdate.visibleCategories) {
      // TODO - this is going to happen again after the transition. Prevent this from happening twice for no reason.
      this.onPerformDomCalculations(stateUpdate);

      // See if anything previously visible is still visible. If so, scroll there.
      for (const spot of this.prevVisibleSpots) {
        if (this.shownSpots.find(s => s === spot)) {
          return spot.scrollPos - BANNER_HEIGHT - 30;
        }
      }

      // Just scroll to the current location.
      if (this.state.currentMile !== CURRENT_MILE_UNKNOWN) {
        const scroll = this.getCurrentLocationScroll();
        if (scroll !== null) {
          return scroll;
        }
      }
    }
    return historyManager.state?.scroll ?? 0;
  }

  protected onPerformDomCalculations(stateUpdate: StateUpdate) {
    this.handleResize(stateUpdate, true);
  }

  protected onActive(stateUpdate: StateUpdate) {
    this.toggleScrollIndicatorFromLocation();
  }

  protected onActiveStateUpdate(stateUpdate: StateUpdate) {
    this.renderStateUpdate(stateUpdate);
    this.handleResize(stateUpdate);
    this.toggleScrollIndicatorFromLocation();
  }

  private renderStateUpdate(stateUpdate: StateUpdate) {
    if (isSet(stateUpdate.isRouteReversed)) {
      const spotsEl = getById('spots');
      for (let i = 1; i < spotsEl.childNodes.length; i++) {
        spotsEl.insertBefore(spotsEl.childNodes[i], spotsEl.firstChild);
      }
      this.spots.reverse();
      // Deal with the fact that we hide the starting point spot.
      this.hideStartingPointSpot();
      setSpotShown(this.spots[this.spots.length - 1],
        this.state.visibleCategories.has(this.spots[this.spots.length - 1].metadata.categoryName ?? ''));

      this.populateShownSpots();
    }

    if (isSet(stateUpdate.visibleCategories)) {
      this.toggleVisibleCategories();
    }
    if (isSet(stateUpdate.locationUpdating) ||
      isSet(stateUpdate.locationUpdateError)) {
      this.toggleLocationUpdating();
    }
    // Changing categories affects the currentLocation line and distances
    // are only set on visible spots.
    if (isSet(stateUpdate.isRouteReversed) ||
      isSet(stateUpdate.visibleCategories) ||
      isSet(stateUpdate.currentMile) ||
      isSet(stateUpdate.speed)) {
      this.handleUpdatedDistance();
    }
  }

  private toggleScrollIndicatorFromLocation() {
    if (this.state.currentMile === CURRENT_MILE_UNKNOWN) {
      this.scrollIndicator.deactivate();
    } else {
      this.scrollIndicator.activate();
    }
  }

  protected onDeactivate() {
    // This must be called before scrollIndicator.deactivate() or it will reactivate it.
    if (this.scrollAnimatorCancel) this.scrollAnimatorCancel();
    this.scrollIndicator.deactivate();

    this.prevVisibleSpots = [];
    const topVisible = window.scrollY + BANNER_HEIGHT;
    const bottomVisible = window.scrollY + window.innerHeight - 50;
    for (const spot of this.shownSpots) {
      // shownSpots is ordered by scroll position, so once we pass bottom visible, stop the loop.
      if (spot.scrollPos > bottomVisible) break;
      if (spot.scrollPos >= topVisible) {
        this.prevVisibleSpots.push(spot);
      }
    }
  }

  private createIconStyles() {
    const kmz = this.state.kmz;
    const el = document.createElement('style');
    document.head.appendChild(el);
    for (const [iconId, iconPath] of Object.entries(kmz.icons)) {
      el.sheet!.insertRule(`.icon-${iconId}{
        background-image: url(${getRouteImageUrl(iconPath)});
      }`, 0);
    }
  }

  private getCurrentLocationScroll(): number | null {
    const currentLocationEl =
      document.querySelector('.current-location-line.shown');
    if (!currentLocationEl) {
      return null;
    }
    if (currentLocationEl.classList.contains('last-line')) {
      return document.body.scrollHeight;
    }
    return currentLocationEl.parentElement!.offsetTop - 130;
  }

  private handleResize(stateUpdate: StateUpdate, force = false) {
    if (!this.isActive && !force) {
      return;
    }
    this.windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    if (windowWidth === this.windowWidth &&
      !isSet(stateUpdate.visibleCategories) &&
      !isSet(stateUpdate.isRouteReversed)) {
      return;
    }
    this.windowWidth = windowWidth;
    for (const spot of this.shownSpots) {
      spot.scrollPos = spot.el.offsetTop;
    }
  }

  private getScrollIndicatorText(scrollY: number): string | null {
    // Find the distance.
    const spot = findClosest(this.shownSpots,
      scrollY + (this.windowHeight / 2));
    if (!spot) {
      // Not sure why this can happen.
      return null;
    }
    const distance = spot.metadata.mile - this.state.currentMile;
    return (distance > 0 ? '+' : '') + Math.round(distance);
  }

  private toggleLocationUpdating() {
    setShown(querySelector('#route .location-pending'), this.state.locationUpdating);
    setShown(querySelector('#route .location-error'), this.state.locationUpdateError);
  }

  /*
   * This must be called before handleUpdatedDistance() because it populates
   * visibleSpots.
   */
  private toggleVisibleCategories() {
    const visibleCategories = this.state.visibleCategories;
    for (const spot of this.spots) {
      setSpotShown(spot, visibleCategories.has(spot.metadata.categoryName ?? ''));
    }
    this.hideStartingPointSpot();
    this.populateShownSpots();
  }

  private handleUpdatedDistance() {
    if (this.state.currentMile === CURRENT_MILE_UNKNOWN) {
      return;
    }

    let placedLocationLine = false;
    removeCurrentLocationLine();

    // In the loop below, we check to see where to put the location line by finding
    // which spot is past it. If we're already past the end of the route, then we'll
    // never find such a spot.
    const lastSpot = this.spots[this.spots.length - 1];
    if (lastSpot.metadata.mile < this.state.currentMile) {
      this.el.querySelector('.current-location-line.last-line')!.classList.add('shown');
      placedLocationLine = true;
    }

    for (const spot of this.spots) {
      if (!spot.shown) {
        continue;
      }
      const distance = spot.metadata.mile - this.state.currentMile;
      spot.distanceEl.textContent = roundToDecimal(distance, 1);
      const milesFromExit = spot.metadata.milesFromExit || 0;
      spot.etaEl.textContent = this.calculateEta(Math.abs(distance) + milesFromExit);
      if (!placedLocationLine && distance >= 0) {
        placeLocationLine(spot);
        placedLocationLine = true;
      }
    }
    this.updateMileMarker();
  }

  private calculateEta(distance: number): string {
    const hours = distance / this.state.speed;
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    let text = '';
    if (wholeHours > 0) {
      text = wholeHours + 'h ';
    }
    text += minutes + 'm';

    const eta = new Date(Date.now() + (hours * HOURS_PER_MS));
    const now = new Date();
    // Only show time of arrival if its today.
    if (eta.getDate() === now.getDate()) {
      let time = eta.toLocaleTimeString();
      // remove the seconds.
      // example format 1:15:30 AM
      let splitBySpace = time.split(' ');
      const sections = splitBySpace[0].split(':');
      splitBySpace[0] = sections[0] + ':' + sections[1];
      time = splitBySpace.join(' ');
      text += ' (' + time + ')';
    }
    return text;
  }

  private toggleMileMarkerMode() {
    this.mileMarkerIsTraveled = !this.mileMarkerIsTraveled;
    const labelEl = getById('mile-marker-label');
    if (this.mileMarkerIsTraveled) {
      labelEl.textContent = 'Miles traveled:';
    } else {
      labelEl.textContent = 'Miles to go:';
    }
    this.updateMileMarker();
  }

  private updateMileMarker() {
    const value = this.mileMarkerIsTraveled ? this.state.currentMile :
      this.state.kmz.totalDistance - this.state.currentMile;
    querySelector('#route .mile-marker-num').textContent =
      roundToDecimal(value, 1);
  }

  /**
   * If the first spot is the stating point, hide it because it's not useful.
   * this.populateShownSpots() must be updated after this.
   */
  private hideStartingPointSpot() {
    const firstSpot = this.spots[0];
    setSpotShown(firstSpot, firstSpot.metadata.mile > 0.1 || !firstSpot.metadata.isRouteStep);
  }

  private populateShownSpots() {
    this.shownSpots = this.spots.filter(s => s.shown);
  }
}

function placeLocationLine(spot: SpotRef) {
  spot.el.querySelector('.current-location-line')!.classList.add('shown');
}

function removeCurrentLocationLine() {
  const currentEl = document.querySelector('.current-location-line.shown');
  if (currentEl) {
    currentEl.classList.remove('shown');
  }
}

// Returns the index of the closest value.
function findClosest(arr: SpotRef[], target: number): SpotRef {
  let upper = arr.length - 1;
  let lower = 0;
  while (lower <= upper) {
    const mid = Math.floor((lower + upper) / 2);

    if (arr[mid].scrollPos === target) {
      return arr[mid];
    }

    if (arr[mid].scrollPos < target) {
      lower = mid + 1;
    } else {
      upper = mid - 1;
    }
  }
  return arr[upper];
}

function setSpotShown(spot: SpotRef, shown: boolean) {
  spot.shown = shown;
  setShown(spot.el, shown);
}